import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import ToastContainer from '@/components/toast/ToastContainer';

import {
  ModalActionProps,
  ModalAlign,
  ModalBackdropProps,
  ModalBoxProps,
  ModalCloseProps,
  ModalPosition,
  ModalProps,
} from './types';
import { Button } from '../button';

const positionClasses: Record<ModalPosition, string> = {
  default: '',
  top: 'modal-top',
  bottom: 'modal-bottom',
};

const alignClasses: Record<ModalAlign, string> = {
  default: '',
  start: 'modal-start',
  end: 'modal-end',
};

const ModalBase = forwardRef<HTMLDialogElement, ModalProps>(
  (
    { className, position = 'default', align = 'default', children, ...props },
    ref,
  ) => {
    const classes = twMerge(
      'modal',
      positionClasses[position],
      alignClasses[align],
      className,
    );

    return (
      <dialog ref={ref} className={classes} {...props}>
        <ToastContainer />
        {children}
      </dialog>
    );
  },
);

const Box = forwardRef<HTMLDivElement, ModalBoxProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={twMerge('modal-box', className)} {...props}>
      {children}
    </div>
  ),
);

const Action = forwardRef<HTMLDivElement, ModalActionProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={twMerge('modal-action', className)} {...props}>
      {children}
    </div>
  ),
);

const Backdrop = forwardRef<HTMLFormElement, ModalBackdropProps>(
  ({ className, children, ...props }, ref) => (
    <form
      ref={ref}
      method="dialog"
      className={twMerge('modal-backdrop', className)}
      {...props}
    >
      {children ?? <button>close</button>}
    </form>
  ),
);

const Close = forwardRef<HTMLButtonElement, ModalCloseProps>(
  ({ className, children, onClick, ...props }, ref) => {
    const classes = twMerge('absolute right-2 top-2', className);
    const button = (
      <Button
        ref={ref}
        variant="ghost"
        shape="circle"
        className={classes}
        onClick={onClick}
        {...props}
      >
        {children ?? '✕'}
      </Button>
    );

    if (onClick) return button;

    return <form method="dialog">{button}</form>;
  },
);

ModalBase.displayName = 'Modal';
Box.displayName = 'Modal.Box';
Action.displayName = 'Modal.Action';
Close.displayName = 'Modal.Close';
Backdrop.displayName = 'Modal.Backdrop';

export const Modal = Object.assign(ModalBase, {
  Box,
  Action,
  Close,
  Backdrop,
});
